// IP Type specific colors for consistent use across the application
export const IP_TYPE_COLORS = {
  PATENTS: "#4caf50",     // Green for Patents
  TRADEMARKS: "#9c27b0",  // Purple for Trademarks
  CONTRACTS: "#ff9800",   // Orange for Contracts
} as const;

// IP Type colors by ID (commonly used in APIs)
export const IP_TYPE_COLORS_BY_ID = {
  1: IP_TYPE_COLORS.PATENTS,      // Patent type ID
  2: IP_TYPE_COLORS.TRADEMARKS,   // Trademark type ID
  3: IP_TYPE_COLORS.CONTRACTS,    // Contract type ID
} as const;

// Country specific colors
export const COUNTRY_COLORS = {
  CA: "#d32f2f",  // Red for Canada
  US: "#2196f3",  // Blue for United States
  GB: "#4caf50",  // Green for Great Britain
  DE: "#ff5722",  // Deep Orange for Germany
  FR: "#9c27b0",  // Purple for France
  JP: "#795548",  // Brown for Japan
  AU: "#607d8b",  // Blue Grey for Australia
  CN: "#e91e63",  // Pink for China
  IN: "#ff9800",  // Orange for India
  BR: "#8bc34a",  // Light Green for Brazil
} as const;

// Brand colors
export const BRAND_COLORS = {
  PRIMARY: "#1C5195",  // Main Fortress brand color
  SECONDARY: "#DDD8D6", // Secondary brand color
} as const;

// Status colors
export const STATUS_COLORS = {
  OVERDUE: "#d32f2f",   // Red for overdue
  WARNING: "#f57c00",   // Orange for warning/upcoming
  SUCCESS: "#388e3c",   // Green for success/completed
  INFO: "#2196f3",      // Blue for info
  NEUTRAL: "#757575",   // Grey for neutral/expired
} as const;

// IP Status specific colors for different states
export const IP_STATUS_COLORS = {
  // Active/Granted states - Green variants
  ACTIVE: "#4caf50",
  GRANTED: "#388e3c", 
  REGISTERED: "#2e7d32",
  IN_FORCE: "#1b5e20",
  MAINTAINED: "#4caf50",
  ALLOWED: "#66bb6a",
  ISSUED: "#388e3c",
  
  // Pending/Processing states - Blue variants  
  PENDING: "#2196f3",
  FILED: "#1976d2",
  PUBLISHED: "#1565c0",
  UNDER_EXAMINATION: "#0d47a1",
  AWAITING_RESPONSE: "#42a5f5",
  APPLICATION_FILED: "#1976d2",
  PROSECUTION: "#1565c0",
  
  // Warning states - Orange variants
  EXPIRING_SOON: "#ff9800",
  RENEWAL_DUE: "#f57c00",
  ACTION_REQUIRED: "#e65100",
  OPPOSITION_PERIOD: "#ff6f00",
  ANNUITY_DUE: "#ff8f00",
  
  // Expired/Inactive states - Red variants
  EXPIRED: "#f44336",
  ABANDONED: "#d32f2f",
  LAPSED: "#b71c1c",
  CANCELLED: "#c62828",
  REJECTED: "#d50000",
  WITHDRAWN: "#e53935",
  DEAD: "#b71c1c",
  
  // Neutral/Unknown states - Grey variants
  UNKNOWN: "#9e9e9e",
  DRAFT: "#757575",
  ON_HOLD: "#616161",
  SUSPENDED: "#424242",
  NOT_APPLICABLE: "#616161",
} as const;

// Deadline status colors
export const DEADLINE_STATUS_COLORS = {
  PENDING: "#2196f3",     // Blue for pending
  EXPIRED: "#757575",     // Grey for expired  
  COMPLETE: "#388e3c",    // Green for completed
  OVERDUE: "#d32f2f",     // Red for overdue
} as const;

// Fallback colors for dynamic assignments
export const FALLBACK_COLORS = [
  "#757575", "#616161", "#424242", "#263238", "#37474f", 
  "#455a64", "#546e7a", "#78909c", "#90a4ae", "#b0bec5"
] as const;

// Helper functions
export const getIPTypeColor = (typeId: number | string): string => {
  if (typeof typeId === "string") {
    return IP_TYPE_COLORS[typeId.toUpperCase() as keyof typeof IP_TYPE_COLORS] || STATUS_COLORS.NEUTRAL;
  }
  return IP_TYPE_COLORS_BY_ID[typeId as keyof typeof IP_TYPE_COLORS_BY_ID] || STATUS_COLORS.NEUTRAL;
};

export const getCountryColor = (countryCode: string): string => {
  return COUNTRY_COLORS[countryCode as keyof typeof COUNTRY_COLORS] || 
         FALLBACK_COLORS[Math.abs(countryCode.charCodeAt(0)) % FALLBACK_COLORS.length];
};

export const getStatusColor = (index: number): string => {
  return FALLBACK_COLORS[index % FALLBACK_COLORS.length];
};

// Get IP status color based on status text
export const getIPStatusColor = (status: string | null | undefined): string => {
  if (!status) return IP_STATUS_COLORS.UNKNOWN;
  
  const statusUpper = status.toUpperCase().replace(/[\s-_]/g, '_');
  
  // Direct match first
  if (statusUpper in IP_STATUS_COLORS) {
    return IP_STATUS_COLORS[statusUpper as keyof typeof IP_STATUS_COLORS];
  }
  
  // Fuzzy matching for common status patterns
  const statusLower = status.toLowerCase();
  
  // Active/Granted states
  if (statusLower.includes('active') || statusLower.includes('in force')) return IP_STATUS_COLORS.ACTIVE;
  if (statusLower.includes('granted') || statusLower.includes('allowed')) return IP_STATUS_COLORS.GRANTED;
  if (statusLower.includes('registered') || statusLower.includes('issued')) return IP_STATUS_COLORS.REGISTERED;
  if (statusLower.includes('maintained')) return IP_STATUS_COLORS.MAINTAINED;
  
  // Pending/Processing states  
  if (statusLower.includes('pending') || statusLower.includes('application')) return IP_STATUS_COLORS.PENDING;
  if (statusLower.includes('filed')) return IP_STATUS_COLORS.FILED;
  if (statusLower.includes('published') || statusLower.includes('prosecution')) return IP_STATUS_COLORS.PUBLISHED;
  if (statusLower.includes('examination')) return IP_STATUS_COLORS.UNDER_EXAMINATION;
  if (statusLower.includes('awaiting') || statusLower.includes('response')) return IP_STATUS_COLORS.AWAITING_RESPONSE;
  
  // Warning states
  if (statusLower.includes('expiring') || statusLower.includes('due')) return IP_STATUS_COLORS.EXPIRING_SOON;
  if (statusLower.includes('renewal') || statusLower.includes('annuity')) return IP_STATUS_COLORS.RENEWAL_DUE;
  if (statusLower.includes('action') || statusLower.includes('required')) return IP_STATUS_COLORS.ACTION_REQUIRED;
  if (statusLower.includes('opposition')) return IP_STATUS_COLORS.OPPOSITION_PERIOD;
  
  // Expired/Inactive states
  if (statusLower.includes('expired')) return IP_STATUS_COLORS.EXPIRED;
  if (statusLower.includes('abandoned') || statusLower.includes('dead')) return IP_STATUS_COLORS.ABANDONED;
  if (statusLower.includes('lapsed')) return IP_STATUS_COLORS.LAPSED;
  if (statusLower.includes('cancelled')) return IP_STATUS_COLORS.CANCELLED;
  if (statusLower.includes('rejected')) return IP_STATUS_COLORS.REJECTED;
  if (statusLower.includes('withdrawn')) return IP_STATUS_COLORS.WITHDRAWN;
  
  // Neutral states
  if (statusLower.includes('draft')) return IP_STATUS_COLORS.DRAFT;
  if (statusLower.includes('hold')) return IP_STATUS_COLORS.ON_HOLD;
  if (statusLower.includes('suspended')) return IP_STATUS_COLORS.SUSPENDED;
  if (statusLower.includes('not applicable') || statusLower.includes('n/a')) return IP_STATUS_COLORS.NOT_APPLICABLE;
  
  return IP_STATUS_COLORS.UNKNOWN;
};

// Get deadline status color
export const getDeadlineStatusColor = (status: string, date?: string): string => {
  const statusUpper = status.toUpperCase();
  
  // Check for overdue condition
  if (statusUpper === 'PENDING' && date) {
    const deadlineDate = new Date(date);
    const today = new Date();
    if (deadlineDate < today) {
      return DEADLINE_STATUS_COLORS.OVERDUE;
    }
  }
  
  return DEADLINE_STATUS_COLORS[statusUpper as keyof typeof DEADLINE_STATUS_COLORS] || 
         DEADLINE_STATUS_COLORS.PENDING;
};

// Get status chip style object for Material-UI components
export const getStatusChipStyle = (status: string | null | undefined) => {
  const color = getIPStatusColor(status);
  return {
    backgroundColor: `${color}20`, // 20% opacity background
    color: color,
    borderColor: `${color}40`,    // 40% opacity border
  };
}; 